import {useCallback, useMemo} from 'react';

import {Box, Link, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import './styles';
import {
  PAYMENT_LINK_IMAGES,
  PaymentLinkV2Campaign,
} from '@/app/main/paymentLink/models/constants/paymentLink.constants';
import {AppButton, AppButtonVariant} from '@/app/shared-components';
import {RaterComponent} from '@/app/shared-components/Rater';
import {LINKS} from '@/constants/LinksConstants';
import {useAppRates} from '@/dataSource/src/api/firebase/useAppRates';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {COLORS} from '@/styles/colors';

interface PaymentLinkPromoProps {
  utmMedium: string;
  utmCampaign: string;
  isCampaign?: boolean;
}

const APP_RATE_DOCUMENT_IDS = ['appStore', 'trustpilot'];

const PaymentLinkV2Promo = (props: PaymentLinkPromoProps) => {
  const {t} = useTranslation();

  const {getRateByDevice} = useAppRates({
    documentIds: APP_RATE_DOCUMENT_IDS,
  });

  const appStoreRateData = useMemo(
    () => getRateByDevice('appStore', 'mobile'),
    [getRateByDevice],
  );
  const trustpilotRateData = useMemo(
    () => getRateByDevice('trustpilot', 'mobile'),
    [getRateByDevice],
  );

  const {trackSelectRequestMoneyJoinModak} = usePaymentLinkTrackers();

  const handleOpenLink = useCallback(() => {
    const newWindow = window.open(
      LINKS.MODAK_PAYMENT_LINK_GET_STARTED(
        props.utmMedium,
        props.utmCampaign,
        PaymentLinkV2Campaign.CODE,
      ),
      '_blank',
    );
    trackSelectRequestMoneyJoinModak('payment_link_v2_success');
    if (newWindow) {
      newWindow.focus();
      newWindow.opener = null;
    }
  }, [props.utmCampaign, props.utmMedium, trackSelectRequestMoneyJoinModak]);

  const containerClassName = props.isCampaign
    ? 'payment-link-v2-success-promotion-container payment-link-v2-success-promotion-container--campaign'
    : 'payment-link-v2-success-promotion-container';

  return (
    <>
      <Box className={containerClassName}>
        {!props.isCampaign && (
          <Box className="payment-link-v2-success-promotion-image-container">
            <img
              className="payment-link-v2-success-promotion-image"
              src={PAYMENT_LINK_IMAGES.MODAK_WEBSITE_V2_COMPOSITE.src}
              alt={PAYMENT_LINK_IMAGES.MODAK_WEBSITE_V2_COMPOSITE.alt}
            />
          </Box>
        )}

        <Box className="payment-link-v2-success-promotion-content">
          <Typography
            component="h2"
            className="payment-link-v2-success-promotion-title">
            {t('payment_link_v2.success.promotion.title_v3')}
          </Typography>
          <Typography className="payment-link-v2-success-promotion-description">
            {t(
              props.isCampaign
                ? 'payment_link_v2.success.promotion.campaign_description'
                : 'payment_link_v2.success.promotion.description_v3',
              {
                amount: PaymentLinkV2Campaign.AMOUNT,
                code: PaymentLinkV2Campaign.CODE,
              },
            )}
          </Typography>

          <Box className="payment-link-v2-success-promotion-chips">
            <RaterComponent
              text={appStoreRateData?.label ?? ''}
              rating={appStoreRateData?.rate ?? 0}
            />
            <RaterComponent
              text={trustpilotRateData?.label ?? ''}
              rating={trustpilotRateData?.rate ?? 0}
            />
          </Box>

          <Box className="payment-link-v2-success-button-container">
            <AppButton
              id="payment-link-v2-promo-get-started-button"
              removeStyles={true}
              onClick={handleOpenLink}
              label={t(
                props.isCampaign
                  ? 'payment_link_v2.success.promotion.campaign_button'
                  : 'payment_link_v2.success.promotion.button',
              )}
              variant={AppButtonVariant.onPrimary}
              color={props.isCampaign ? COLORS.PASTEL_E : COLORS.ON_SURFACE_1}
              labelColor={props.isCampaign ? COLORS.ON_SURFACE_1 : undefined}
              borderColor={
                props.isCampaign ? COLORS.PASTEL_E : COLORS.ON_SURFACE_1
              }
            />
          </Box>
        </Box>
      </Box>

      <Box className="payment-link-v2-success-disclaimer-container">
        <Typography className="payment-link-v2-success-disclaimer-text">
          {t('payment_link_v2.success.disclaimer.deposit_account')}
        </Typography>
        <Typography className="payment-link-v2-success-disclaimer-text">
          {t('payment_link_v2.success.disclaimer.app_store_ratings')}
        </Typography>
        <Typography className="payment-link-v2-success-disclaimer-text">
          {t('payment_link_v2.success.disclaimer.referral_program_prefix')}
          <Link
            href={LINKS.REFERRAL_PROGRAM}
            className="payment-link-v2-success-disclaimer-text-link"
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            color="inherit">
            {t('payment_link_v2.success.disclaimer.referral_program')}
          </Link>
          {t('payment_link_v2.success.disclaimer.terms_conditions')}
        </Typography>
      </Box>
    </>
  );
};

export default PaymentLinkV2Promo;
